import { useState } from 'react'
import type { InsightDrillTarget } from '../../../../../data/ServerApiClient'
import { formatCurrencyCompact } from '../../cfoFormat'
import type { CeoCadenceItem, CeoCockpitResponse, CeoIncident } from '../ceoCockpitTypes'
import { CeoCardVisualView } from './CeoCardVisual'

export type CeoIncidentAction = 'acknowledge' | 'snooze' | 'assign' | 'resolve'

export type CeoIncidentCardProps = {
  item: CeoCadenceItem
  /** The full payload, for visuals that reference sections it already carries. */
  payload: CeoCockpitResponse
  /** The grouped signals behind this item, when it came from the incident queue. */
  incident?: CeoIncident | null
  rank: number
  /** Highlighted because a callout chip jumped here. */
  focused?: boolean
  readOnly?: boolean
  onAction: (action: CeoIncidentAction, item: CeoCadenceItem) => void
  onShowNumbers: (item: CeoCadenceItem) => void
  onOpenDrill?: (target?: InsightDrillTarget | null) => void
}

const KIND_LABEL: Record<string, string> = {
  decide: 'Decide now',
  watch: 'Watch',
  celebrate: 'Good news',
  note: 'Rhythm',
}

const STATE_SUFFIX: Record<string, string> = {
  new: ' · new',
  worsening: ' · worsening',
  recovering: ' · recovering',
  unchanged: '',
  resolved: ' · resolved',
}

/**
 * One incident: what is happening, what it costs, what should happen next, and
 * who owns it.
 *
 * The underlying signals stay reachable but subordinate. Five warnings about
 * one sales problem is one problem, and listing all five is how a daily page
 * becomes something a reader skims.
 */
export function CeoIncidentCard({
  item,
  payload,
  incident,
  rank,
  focused,
  readOnly = false,
  onAction,
  onShowNumbers,
  onOpenDrill,
}: CeoIncidentCardProps) {
  const [signalsOpen, setSignalsOpen] = useState(false)
  const tone =
    item.kind === 'decide' ? 'bad' : item.kind === 'watch' ? 'watch' : item.kind === 'note' ? 'note' : 'good'
  // A rhythm note is a reading, not work: nothing to assign or acknowledge.
  const actionable = item.kind !== 'note' && !readOnly
  const signals = incident?.signals ?? []

  return (
    <article
      id={`ceo-inc-${item.key}`}
      className={`ceo-incident ${tone}${focused ? ' jumped' : ''}`}
      data-testid={`incident-${item.key}`}
      data-fg-tour={rank === 1 ? 'ceo-decision-card' : undefined}
    >
      <header className="ceo-inc-head">
        <span className="ceo-inc-rank" aria-hidden="true">
          {rank}
        </span>
        <div className="ceo-inc-title">
          <span className={`ceo-inc-kind ${tone}`}>
            {KIND_LABEL[item.kind] ?? item.kind}
            {STATE_SUFFIX[item.state] ?? ''}
          </span>
          <h3>{item.headline}</h3>
          {item.exposure_label ? <p className="ceo-inc-exposure">{item.exposure_label}</p> : null}
        </div>
      </header>

      {item.visual ? (
        <figure className="ceo-inc-visual">
          <CeoCardVisualView visual={item.visual} payload={payload} />
          <figcaption>{item.visual.caption}</figcaption>
        </figure>
      ) : null}

      {item.why ? <p className="ceo-inc-why">{item.why}</p> : null}

      {item.next_step ? (
        <p className="ceo-inc-next">
          <span className="ceo-cap">Next</span> {item.next_step}
          {item.owner ? <span className="ceo-inc-owner">{item.owner}</span> : null}
        </p>
      ) : null}

      <div className="ceo-inc-acts">
        <button type="button" className="cfo-btn sm primary" onClick={() => onShowNumbers(item)}>
          Show me the numbers
        </button>
        {actionable ? (
          <>
            <button type="button" className="cfo-btn sm" onClick={() => onAction('assign', item)}>
              Assign
            </button>
            <button type="button" className="cfo-btn sm" onClick={() => onAction('acknowledge', item)}>
              Acknowledge
            </button>
          </>
        ) : null}
        {signals.length > 1 ? (
          <button
            type="button"
            className="cfo-btn sm"
            aria-expanded={signalsOpen}
            onClick={() => setSignalsOpen((open) => !open)}
          >
            {signalsOpen ? 'Hide' : 'Show'} {signals.length} underlying signals
          </button>
        ) : null}
      </div>

      {signalsOpen && signals.length > 1 ? (
        <ul className="ceo-inc-signals">
          {signals.map((signal) => (
            <li key={signal.id}>
              <button
                type="button"
                className="ceo-inc-signal"
                onClick={() => onOpenDrill?.(signal.drill)}
              >
                <span className="ceo-inc-signal-headline">{signal.headline}</span>
                {signal.evidence.length > 0 ? (
                  <span className="ceo-inc-signal-evidence">
                    {signal.evidence.map((chip) => (
                      <span key={`${signal.id}-${chip.label}`}>
                        {chip.label} <b>{chip.display}</b>
                      </span>
                    ))}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  )
}

/**
 * The zero state has to prove it looked.
 *
 * "Nothing needs a decision" without a scope is indistinguishable from a page
 * that failed to load, and it is the single sentence a reader has to believe
 * for the product to be worth opening.
 */
/**
 * "Aug 18", to match the trust line above it. The card was printing a raw
 * `2026-08-18` directly beneath "Through yesterday, Aug 18", which reads as two
 * different dates rather than one.
 */
function shortDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function CeoQuietCard({
  areas,
  through,
  cadence,
  alreadyShown = 0,
}: {
  areas: number
  through: string
  cadence: 'today' | 'week' | 'month'
  /** Findings this rhythm produced that are already on a more urgent horizon. */
  alreadyShown?: number
}) {
  const line =
    cadence === 'today'
      ? 'Nothing needs a decision this morning.'
      : alreadyShown > 0
        ? `Nothing new at this rhythm — what it found is already on Today.`
        : cadence === 'week'
          ? 'Nothing is drifting this week.'
          : 'Nothing changed materially in the closed books.'
  return (
    <article
      className="ceo-incident quiet"
      data-testid="ceo-decision-card"
      data-fg-tour="ceo-decision-card"
    >
      <header className="ceo-inc-head">
        <span className="ceo-inc-rank" aria-hidden="true">
          ✓
        </span>
        <div className="ceo-inc-title">
          <span className="ceo-inc-kind good">All clear</span>
          <h3>{line}</h3>
          <p className="ceo-inc-exposure">
            {alreadyShown > 0 ? (
              <>
                Checked {areas} area{areas === 1 ? '' : 's'} through {shortDate(through)}. {alreadyShown}{' '}
                finding{alreadyShown === 1 ? '' : 's'} at this rhythm {alreadyShown === 1 ? 'is' : 'are'}{' '}
                already on Today, and a number is never reported twice under two names.
              </>
            ) : (
              <>
                Checked {areas} area{areas === 1 ? '' : 's'} through {shortDate(through)}; no
                approved threshold is breached.
              </>
            )}
          </p>
        </div>
      </header>
    </article>
  )
}

/** Exposure for the compact listings that show many incidents at once. */
export function exposureDisplay(item: CeoCadenceItem): string {
  if (typeof item.exposure_value === 'number' && Number.isFinite(item.exposure_value)) {
    return formatCurrencyCompact(item.exposure_value)
  }
  return '—'
}
